import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, X, Loader2 } from 'lucide-react';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { useSenzaRete } from '../../hooks/useSenzaRete';
import './FoglioGioca.css';

/**
 * Il VESTITO dei fogli della barra: il pannello che sale dal basso da una voce,
 * con la sua testata e le sue righe.
 *
 * Non e' un foglio: e' la forma che i fogli hanno. Due la usano — «Gioca»
 * (FoglioGioca.tsx) e «Strumenti» (FoglioStrumenti.tsx) — e la lista di righe
 * resta a casa loro, perche' e' l'unica cosa che li distingue davvero.
 *
 * Qui dentro c'e' tutto il resto, cioe' la parte che si sbaglia: i QUATTRO modi
 * di chiudere (la X, il velo, Escape, il gesto indietro), il fuoco che entra e
 * non scappa, la riga che si spegne senza rete. Scritta due volte, sarebbe
 * andata in deriva alla prima correzione — e la prima correzione di questo
 * pannello e' costata il difetto descritto sopra `dalFoglioGioca` in App.tsx.
 *
 * ⚠️ Le classi si chiamano ancora `.foglio-gioca*` e il foglio di stile ancora
 * `FoglioGioca.css`: sono i nomi di quando il pannello era di «Gioca» e basta.
 * Rinominarli non cambierebbe un pixel e costerebbe le 25 guardie di
 * `foglio-gioca.stile.test.ts`, piu' il selettore `body:has(.foglio-gioca)
 * .sidebar` che solleva la barra sopra il velo. Il nome e' storico; il vestito
 * e' comune.
 */
export interface Voce {
  id: string;
  icona: ReactNode;
  etichetta: string;
  azione: () => void;
  primaria?: boolean;
  /**
   * La porta ha bisogno della rete: senza, la riga si spegne.
   *
   * Nel foglio di «Gioca» quattro righe su sei ce l'hanno, e le due che NON ce
   * l'hanno — la sfida al computer e la partita in presenza — girano tutte sul
   * dispositivo, che e' esattamente cio' che l'app promette gia' a parole in
   * `senzaRete.home`. Nel foglio degli strumenti non ce l'ha nessuna: quei tre
   * attrezzi sono tutti di qua.
   */
  serveRete?: boolean;
  /** Questa porta sta aprendo: l'etichetta diventa «Connessione...». */
  inCorso?: boolean;
  /** Il motivo del rifiuto, da mostrare subito sotto questa riga. */
  erroreKey?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Il nome del foglio: lo legge la testata, e lo rilegge `aria-label`. */
  titolo: string;
  /** L'aggancio dei test comportamentali: `foglio-gioca`, `foglio-strumenti`. */
  testId: string;
  voci: Voce[];
}

export default function FoglioMenu({ isOpen, onClose, titolo, testId, voci }: Props) {
  const { t } = useTranslation();

  // Prima del `return null`: gli hook non si saltano, o al primo `isOpen` che
  // cambia React vede «più hook del render precedente».
  useOverlayBackButton(isOpen, onClose);
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen, onClose);
  /*
   * `useSenzaRete` e non lo stato del socket (`isConnected()`,
   * `multiplayer-store.status`): quello e' il meccanismo dell'in-partita, con
   * due secondi di rimbalzo perche' un socket in riconnessione sfarfalla. Qui
   * si guarda il sistema operativo, che non lo fa, e lo si legge DURANTE il
   * primo render — la PWA aperta senza rete non mostra un frame di menu acceso
   * prima di spegnerlo.
   */
  const senzaRete = useSenzaRete();

  if (!isOpen) return null;

  return (
    <div className="foglio-gioca" data-testid={testId}>
      <div className="foglio-gioca-velo" onClick={onClose} aria-hidden="true" />
      <div
        className="foglio-gioca-in"
        ref={riferimentoDialogo}
        role="dialog"
        /*
         * `role="dialog"` SENZA `aria-modal`, ed e' una differenza vera.
         *
         * `aria-modal="true"` vuol dire «tutto il resto e' inerte», e da quando
         * la barra in basso resta accesa e toccabile sopra il velo
         * (FoglioGioca.css) non lo e' piu': dichiararlo nasconderebbe a uno
         * screen reader una barra che sullo schermo si vede e si usa.
         *
         * Il resto dell'apparato modale resta: il fuoco entra qui, Tab non
         * scappa dietro il velo, Escape chiude (`useModaleAccessibile`) e il
         * gesto indietro chiude (`useOverlayBackButton`).
         */
        aria-label={titolo}
        tabIndex={-1}
      >
        <div className="foglio-gioca-testa">
          <h2 className="foglio-gioca-titolo">{titolo}</h2>
          <button
            type="button"
            className="foglio-gioca-chiudi"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/*
          * La ragione PRIMA dei bottoni grigi.
          *
          * Da solo, un bottone spento invita il tocco che una frase non invita
          * — e' scritto in PlayOverlay.css, dove una riga sbarrata e'
          * volutamente NON un bottone disabilitato. Qui pero' le righe restano
          * bottoni (si spengono e si riaccendono da sole con la rete), quindi
          * la frase va messa: senza, quattro righe su sei sono grigie e nessuno
          * dice perche'.
          *
          * L'avviso lungo, `AvvisoSenzaRete`, sta sulla home ed e' in flusso:
          * questo foglio ha un velo che lo copre. La riga qui e' la sua eco
          * corta, e riusa la stessa chiave gia' tradotta in otto lingue.
          *
          * Solo se c'e' qualcosa da spegnere: il foglio degli strumenti non ha
          * nessuna riga che chieda la rete, e li' questa frase direbbe «guarda
          * che non funziona niente» davanti a tre cose che funzionano tutte.
          */}
        {senzaRete && voci.some((v) => v.serveRete) && (
          <p className="foglio-gioca-senzarete" role="status">
            <WifiOff size={16} aria-hidden="true" />
            {t('senzaRete.titolo')}
          </p>
        )}

        {voci.map((voce) => {
          const spenta = Boolean(voce.serveRete) && senzaRete;
          /* Senza rete non c'e' nessuna attesa da raccontare: quella riga e'
             spenta e basta, o direbbe due cose insieme. */
          const inCorso = Boolean(voce.inCorso) && !spenta;
          return (
            <Fragment key={voce.id}>
              <button
                type="button"
                /* `primaria && !spenta`: il gradiente verde al 50% di opacita'
                   continuerebbe a dire «premi qui», che e' il contrario del
                   punto. Da spenta, la prima riga e' una riga come le altre.
                   In ATTESA il verde invece resta: non e' una porta chiusa, e'
                   la riga che l'utente ha appena premuto, e dice «e' questa,
                   sto arrivando». Lo stesso che fa la CTA della hero, che non
                   si scolora mentre connette. */
                className={`foglio-gioca-voce${voce.primaria && !spenta ? ' foglio-gioca-voce-primaria' : ''}${spenta ? ' foglio-gioca-voce-spenta' : ''}`}
                data-voce={voce.id}
                /* L'attributo nativo, non il solo colore: e' cio' che toglie
                   davvero il tocco E la riga dal giro del Tab. La classe gli fa
                   da specchio per il CSS, come in Sidebar.tsx.
                   In attesa non e' cosmetica: il limitatore lato server concede
                   3 ingressi da ospite ogni 15 minuti per IP, e un doppio tocco
                   ne spenderebbe due per una partita sola. */
                disabled={spenta || inCorso}
                aria-busy={inCorso || undefined}
                onClick={voce.azione}
              >
                <span className="foglio-gioca-ico" aria-hidden="true">
                  {inCorso ? <Loader2 size={20} className="foglio-gioca-gira" /> : voce.icona}
                </span>
                <span className="foglio-gioca-etichetta">
                  {/* Chiave gia' tradotta in otto lingue, la stessa che dice
                      l'attesa nella CTA della hero. */}
                  {inCorso ? t('online.connecting') : voce.etichetta}
                </span>
              </button>
              {/* Il motivo del rifiuto sta SOTTO la sua riga, non in cima al
                  foglio: e' la risposta a un tocco, e la risposta sta dove il
                  tocco e' stato dato. `role="alert"` perche' compare dopo. */}
              {voce.erroreKey && (
                <p className="foglio-gioca-errore" role="alert">{t(voce.erroreKey)}</p>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
